import { Modal, NumberInput } from '@mantine/core';
import { IconArrowRight, IconInfoCircle } from '@tabler/icons-react';
import type { BlockStorageQuota, BlockVolume } from '@xver/apis/v1/block-storage/model';
import { Button } from '@xver/components/Atoms';
import {
    maxVolumeSize,
    MIN_VOLUME_GIB,
    resizeBlockedReason,
    volumeSizeError,
} from '@xver/utils/blockStorage';
import { FormEvent, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { formatBaht, volumeMonthlyCost } from '../../ContainerEngineInstancePage/utils/volumeCost';
import {
    useBlockStorageErrorText,
    useBlockVolumeMutations,
} from '../hooks/useBlockVolumeMutations';
import { useBlockStorageRate } from '../hooks/useBlockStorageRate';
import { NUMBER_INPUT_STYLES } from './CreateVolumeModal';

interface ResizeVolumeModalProps {
    /** null = closed. */
    volume: BlockVolume | null;
    /** The owner's limits and usage, from `resolveQuota`. */
    quota: BlockStorageQuota;
    onClose: () => void;
}

export default function ResizeVolumeModal({ volume, quota, onClose }: ResizeVolumeModalProps) {
    const { t } = useTranslation(['blockStorage', 'common']);
    const errorText = useBlockStorageErrorText();
    const { resizeVolume } = useBlockVolumeMutations();
    const rate = useBlockStorageRate();
    const sizeRef = useRef<HTMLInputElement>(null);
    const descriptionId = useId();

    const currentSize = volume?.sizeGiB ?? 0;
    const [sizeGiB, setSizeGiB] = useState(currentSize + 1);
    const [showErrors, setShowErrors] = useState(false);

    const { reset } = resizeVolume;
    useEffect(() => {
        if (!volume) return;
        setSizeGiB(volume.sizeGiB + 1);
        setShowErrors(false);
        reset();
    }, [volume, reset]);

    const limit = maxVolumeSize(quota, currentSize);
    const blocked = volume ? resizeBlockedReason(quota, currentSize) : null;
    const newSize = blocked ? currentSize : sizeGiB;
    const sizeError = volume ? volumeSizeError(sizeGiB, limit, currentSize) : null;
    const isPending = resizeVolume.isPending;
    const attachment = volume?.attachment;

    const handleSubmit = (event: FormEvent) => {
        event.preventDefault();
        if (!volume || isPending || blocked) return;
        if (sizeError) {
            setShowErrors(true);
            sizeRef.current?.focus();
            return;
        }
        resizeVolume.mutate({ volume, sizeGiB }, { onSuccess: onClose });
    };

    const handleClose = () => {
        if (!isPending) onClose();
    };

    const filesystemNote =
        attachment?.type === 'vm'
            ? t('resize.noteVm', { devicePath: attachment.devicePath })
            : attachment?.type === 'container'
              ? t('resize.noteContainer')
              : t('resize.noteDetached');

    return (
        <Modal
            size="md"
            opened={!!volume}
            onClose={handleClose}
            centered
            closeOnClickOutside={!isPending}
            closeOnEscape={!isPending}
            closeButtonProps={{ 'aria-label': t('common:actions.close'), disabled: isPending }}
            title={
                <span className="text-base font-bold">
                    {t('resize.title', { name: volume?.name ?? '' })}
                </span>
            }
            overlayProps={{ backgroundOpacity: 0.55, blur: 3 }}
            transitionProps={{ transition: 'fade', duration: 128, timingFunction: 'linear' }}
        >
            <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
                <div className="w-full flex flex-row flex-wrap items-center justify-center gap-4 p-3 rounded-md bg-fg/5">
                    <div className="flex flex-col items-center">
                        <span className="text-xs text-fg/68">{t('resize.current')}</span>
                        <span className="text-sm font-mono text-fg">
                            {t('sizeValue', { size: currentSize })} ·{' '}
                            {t('perMonth', {
                                amount: formatBaht(volumeMonthlyCost(currentSize, rate)),
                            })}
                        </span>
                    </div>
                    <IconArrowRight size={18} className="text-fg/58" aria-hidden="true" />
                    <div className="flex flex-col items-center">
                        <span className="text-xs text-fg/68">{t('resize.new')}</span>
                        <span className="text-sm font-mono text-fg">
                            {t('sizeValue', { size: newSize })} ·{' '}
                            {t('perMonth', {
                                amount: formatBaht(volumeMonthlyCost(newSize, rate)),
                            })}
                        </span>
                    </div>
                </div>

                <NumberInput
                    ref={sizeRef}
                    data-autofocus
                    size="md"
                    label={t('fields.size')}
                    description={
                        blocked
                            ? t(`blocked.resize.${blocked}`, {
                                  max: quota.maxVolumeGiB,
                                  limit: quota.limitGiB,
                              })
                            : t(`resize.sizeLimit.${limit.binding}`, {
                                  max: limit.max,
                                  limit: quota.limitGiB,
                              })
                    }
                    descriptionProps={{ id: descriptionId }}
                    min={blocked ? undefined : currentSize + 1}
                    max={blocked ? undefined : limit.max}
                    clampBehavior="none"
                    allowDecimal={false}
                    allowNegative={false}
                    value={newSize}
                    onChange={(value) => setSizeGiB(Number(value))}
                    error={
                        showErrors && sizeError && !blocked
                            ? t(`sizeErrors.${sizeError}`, {
                                  min: MIN_VOLUME_GIB,
                                  max: limit.max,
                                  current: currentSize,
                                  used: quota.usedGiB,
                                  limit: quota.limitGiB,
                              })
                            : undefined
                    }
                    readOnly={isPending || !!blocked}
                    styles={NUMBER_INPUT_STYLES}
                />

                <div className="flex flex-row items-start gap-2">
                    <IconInfoCircle
                        size={16}
                        className="shrink-0 mt-0.5 text-fg/58"
                        aria-hidden="true"
                    />
                    <p className="text-sm text-fg/78 break-all sm:break-normal">{filesystemNote}</p>
                </div>

                {resizeVolume.error && (
                    <p role="alert" className="text-sm text-fg">
                        {errorText(resizeVolume.error)}
                    </p>
                )}

                <div className="flex flex-wrap justify-end gap-2">
                    <Button variant="secondary" onClick={handleClose} disabled={isPending}>
                        {t('common:actions.cancel')}
                    </Button>
                    <Button
                        type="submit"
                        isLoading={isPending}
                        aria-disabled={blocked ? true : undefined}
                        aria-describedby={blocked ? descriptionId : undefined}
                        style={blocked ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                    >
                        {t('resize.submit')}
                    </Button>
                </div>
            </form>
        </Modal>
    );
}
